import * as amplitude from '@amplitude/analytics-browser';
import { AMPLITUDE_API_KEY, ENV, STAGE } from '$lib/env';
import { authentication } from '$lib/authentication';

let initialized = false;

/**
 * Hash a string using SHA-256 and return the first 16 hex characters.
 * This matches the approach used by Service Portal for consistent user identification.
 */
async function hashUserId(userId: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(userId);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .substring(0, 16);
}

/**
 * Initialize Amplitude analytics.
 * Only initializes in environments where AMPLITUDE_API_KEY is set (eu-test, eu-prod).
 * Must be called after authentication is ready.
 */
export async function initAnalytics(): Promise<void> {
  if (initialized) {
    return;
  }

  if (!AMPLITUDE_API_KEY) {
    console.log('[Analytics] Disabled - no API key configured');
    return;
  }

  try {
    amplitude.init(AMPLITUDE_API_KEY, {
      serverZone: 'EU',
      defaultTracking: {
        pageViews: false,
        sessions: true,
        formInteractions: false,
        fileDownloads: false
      },
      trackingOptions: {
        ipAddress: false
      }
    });

    // Set user ID from Keycloak token
    const token = authentication.getParsedAccessToken();
    if (token?.sub) {
      const hashedUserId = await hashUserId(token.sub);
      amplitude.setUserId(hashedUserId);
    }

    // Set user properties
    const identify = new amplitude.Identify();
    identify.set('app_name', 'rsm-ui');
    identify.set('app_environment', `${ENV}-${STAGE}`);
    amplitude.identify(identify);

    initialized = true;
    console.log(`[Analytics] Initialized for ${ENV}-${STAGE}`);
  } catch (error) {
    console.warn('[Analytics] Failed to initialize:', error);
  }
}

/**
 * Track a page view event.
 * @param pagePath - The current page path (e.g., '/NO/installations')
 * @param country - The selected country code (e.g., 'NO')
 */
export function trackPageView(pagePath: string, country: string): void {
  if (!initialized || !AMPLITUDE_API_KEY) {
    return;
  }

  const localHour = new Date().getHours();

  amplitude.track('Page Viewed', {
    page_path: pagePath,
    country,
    local_hour: localHour
  });
}

/**
 * Set the selected country (RVM dataset) as a user property.
 * IP-based geolocation is disabled, so Amplitude's built-in Country property
 * is empty. This property reflects which country's data the user is working on.
 * @param country - The selected country code (e.g., 'NO')
 */
export function setUserCountry(country: string): void {
  if (!initialized || !AMPLITUDE_API_KEY) {
    return;
  }

  const identify = new amplitude.Identify();
  identify.set('selected_country', country);
  amplitude.identify(identify);
}
